<template>
  <div class="reset-password-container">
    <div class="reset-password-card">
      <img class="logo" :src="require('../../assets/image/logo.png')" />

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <i class="el-icon-loading"></i>
        <p>Verifying your reset link...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <i class="el-icon-warning-outline"></i>
        <h2>Invalid Reset Link</h2>
        <p>{{ errorMessage }}</p>
        <el-button type="primary" @click="goToLogin">Back to Login</el-button>
      </div>

      <!-- Success State -->
      <div v-else-if="success" class="success-state">
        <i class="el-icon-circle-check"></i>
        <h2>Password Reset Successful!</h2>
        <p>Your password has been updated successfully. You can now log in with your new password.</p>
        <el-button type="primary" @click="goToLogin">Go to Login</el-button>
      </div>

      <!-- Reset Form -->
      <div v-else class="reset-form">
        <h2>Reset Your Password</h2>
        <p class="email-info">Resetting password for: <strong>{{ email }}</strong></p>

        <el-form ref="resetForm" :model="form" :rules="rules" label-position="top">
          <el-form-item label="New Password" prop="newPassword">
            <el-input
              v-model="form.newPassword"
              type="password"
              placeholder="Enter your new password"
              show-password
            ></el-input>
          </el-form-item>

          <el-form-item label="Confirm Password" prop="confirmPassword">
            <el-input
              v-model="form.confirmPassword"
              type="password"
              placeholder="Confirm your new password"
              show-password
            ></el-input>
          </el-form-item>

          <div class="password-requirements">
            <p>Password must:</p>
            <ul>
              <li :class="{ valid: passwordLength }">Be at least 8 characters long</li>
            </ul>
          </div>

          <el-form-item>
            <el-button
              type="primary"
              :loading="submitting"
              @click="submitForm"
              style="width: 100%"
            >
              Reset Password
            </el-button>
          </el-form-item>
        </el-form>

        <div class="back-to-login">
          <span @click="goToLogin">Back to Login</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { verifyPasswordResetToken, updatePassword } from '../../api/eae'

export default {
  name: 'ResetPassword',
  data() {
    const validatePassword = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('Please enter your new password'))
      } else if (value.length < 8) {
        callback(new Error('Password must be at least 8 characters'))
      } else {
        if (this.form.confirmPassword !== '') {
          this.$refs.resetForm.validateField('confirmPassword')
        }
        callback()
      }
    }
    const validateConfirmPassword = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('Please confirm your password'))
      } else if (value !== this.form.newPassword) {
        callback(new Error('Passwords do not match'))
      } else {
        callback()
      }
    }
    return {
      loading: true,
      error: false,
      errorMessage: '',
      success: false,
      submitting: false,
      token: '',
      userType: '',
      userId: '',
      email: '',
      form: {
        newPassword: '',
        confirmPassword: ''
      },
      rules: {
        newPassword: [
          { required: true, validator: validatePassword, trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, validator: validateConfirmPassword, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    passwordLength() {
      return this.form.newPassword.length >= 8
    }
  },
  mounted() {
    // Get query parameters
    this.token = this.$route.query.token
    this.userType = this.$route.query.type
    this.userId = this.$route.query.id

    // Validate parameters
    if (!this.token || !this.userType || !this.userId) {
      this.loading = false
      this.error = true
      this.errorMessage = 'Invalid reset link. Please request a new password reset link.'
      return
    }

    // Verify token
    this.verifyToken()
  },
  methods: {
    verifyToken() {
      verifyPasswordResetToken(this.token, this.userType, this.userId)
        .then(res => {
          this.loading = false
          if (res.data.code === 0) {
            this.email = res.data.data.email
          } else {
            this.error = true
            this.errorMessage = res.data.msg || 'This reset link is invalid or has expired. Please request a new one.'
          }
        })
        .catch(err => {
          this.loading = false
          this.error = true
          this.errorMessage = 'Unable to verify reset link. Please try again later.'
        })
    },
    submitForm() {
      this.$refs.resetForm.validate(valid => {
        if (valid) {
          this.submitting = true
          updatePassword(this.token, this.userType, this.userId, this.form.newPassword)
            .then(res => {
              this.submitting = false
              if (res.data.code === 0) {
                this.success = true
              } else {
                this.$message.error(res.data.msg || 'Failed to reset password')
              }
            })
            .catch(err => {
              this.submitting = false
              this.$message.error('Network error. Please try again.')
            })
        }
      })
    },
    goToLogin() {
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped lang="scss">
.reset-password-container {
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url("~@/assets/image/loginBg.png") no-repeat center center fixed;
  background-size: cover;
  padding: 20px;
  box-sizing: border-box;
}

.reset-password-card {
  background: white;
  border-radius: 10px;
  padding: 40px;
  max-width: 450px;
  width: 100%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.logo {
  width: 180px;
  margin-bottom: 30px;
}

.loading-state,
.error-state,
.success-state {
  padding: 20px 0;

  i {
    font-size: 60px;
    margin-bottom: 20px;
  }

  h2 {
    margin-bottom: 15px;
    color: #333;
  }

  p {
    color: #666;
    margin-bottom: 25px;
    line-height: 1.6;
  }
}

.loading-state {
  i {
    color: #409EFF;
  }
}

.error-state {
  i {
    color: #F56C6C;
  }
}

.success-state {
  i {
    color: #67C23A;
  }
}

.reset-form {
  text-align: left;

  h2 {
    text-align: center;
    margin-bottom: 10px;
    color: #333;
  }

  .email-info {
    text-align: center;
    color: #666;
    margin-bottom: 25px;
    font-size: 14px;
  }

  /deep/.el-form-item__label {
    padding-bottom: 5px;
  }

  /deep/.el-input__inner {
    border-radius: 8px;
  }
}

.password-requirements {
  background: #f5f7fa;
  padding: 12px 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;

  p {
    margin: 0 0 8px 0;
    color: #666;
    font-weight: 500;
  }

  ul {
    margin: 0;
    padding-left: 20px;

    li {
      color: #909399;
      margin-bottom: 4px;

      &.valid {
        color: #67C23A;

        &::before {
          content: '✓ ';
        }
      }
    }
  }
}

.back-to-login {
  text-align: center;
  margin-top: 20px;

  span {
    color: #409EFF;
    cursor: pointer;
    font-size: 14px;

    &:hover {
      text-decoration: underline;
    }
  }
}

@media (max-width: 480px) {
  .reset-password-card {
    padding: 25px;
  }

  .logo {
    width: 140px;
    margin-bottom: 20px;
  }
}
</style>
